import React from 'react';
import {SetOptional} from 'type-fest';

import {CloseDefaultIcon} from '../../icons';
import {CloseButtonView} from './CloseButton.styles';
import type {IconSize} from '../../icons/Icon/Icon';
import type {IconButtonProps} from '../IconButton';

export type CloseButtonProps = {
  /**
   * Adjusts the size of the CloseDefault icon.
   */
  iconSize?: IconSize;
} & SetOptional<Omit<IconButtonProps, 'Icon' | 'variant'>, 'label'>;

/**
 * # CloseButton
 *
 * CloseButton is a common Button used for closing or dismissing components. It
 * is used to handle the close functionality in feedback and overlay components
 * like `Note` and `Modals`.
 *
 * ## Usage
 *
 * ```jsx
 * import {CloseButton} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return <CloseButton />;
 * };
 * ```
 *
 * @component
 */
export const CloseButton = React.forwardRef<
  HTMLButtonElement,
  CloseButtonProps
>(({label = 'Close', ...props}: CloseButtonProps, ref) => {
  return (
    <CloseButtonView
      density="compact"
      label={label}
      {...props}
      Icon={CloseDefaultIcon}
      ref={ref}
      variant="ghost"
    />
  );
});

CloseButton.displayName = 'CloseButton';
CloseButton.toString = CloseButtonView.toString;

export default CloseButton;
